// @flow
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames/bind';
import update from 'immutability-helper';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import buttonStyle from '../../../../../assets/styles/button.scss';
import Loading from '../../../../../elements/Loading/Loading';
import { SingleProductRule, SingleProductViewFile } from '../../../../../elements/SingleProduct/SingleProduct';
import ReportModalContainer from '../../../../report/container/ReportModalContainer';
import StateInfoBox from '../../../components/StateInfoBox/StateInfoBox';
import Modal from '../../../../../elements/Modal/Modal';

import style from './CourierFlow.scss';
import inputStyle from '../../../../../assets/styles/input.scss';
import commonStyle from '../../../components/OrderCommon.scss';
import courierFlowImage from '../../../../../assets/img/order/purchase/state-info/courier-flow.png';
import amazonTrackingUrlLocation
  from '../../../../../assets/img/order/purchase/state-info/amazon-tracking-url-location.jpg';

import type { HTTPError } from '../../../../../error';
import type { LocaleState } from '../../../../locale/duck';
import type { Item } from '../../../../../elements/SingleProduct/SingleProduct';
import type { Node } from 'react';
import type { Courier, Order, OrderItem } from '../../../../../entities';
import type { Motive, MotiveKey } from '../../../../report/duck';
import { FormValidator } from '../../../../../utils/form';

type Props = {
  +buildStatusBoard: () => Node;
  +buildProducts: (renderContent?: (item: Item) => Node) => Node;
  +buildSummary: () => Node;
  +order: Order;
  +locale: LocaleState;
  +setTrackingNumber: (
    orderId: string,
    itemNumber: number,
    trackingCourier: string,
    trackingNumber: string,
    trackingUrl?: string,
  ) => void;
  +itemTrackingNumberLoadingMap: {
    [key: string]: boolean;
  };
  +itemTrackingNumberErrorMap: {
    [key: string]: HTTPError;
  };
  +motives?: { [key: MotiveKey]: Motive[] };
};

type State = {
  itemValues: { [key: string]: { [key: string]: string } };
  reportModalVisible: boolean;
  urlHelperModalVisible: boolean;
};

export default class CourierFlow extends Component<Props, State> {
  changeTrackingSubject$: Subject = new Subject();
  changeTrackingSubscription: Subscription;
  state = {
    itemValues: {},
    reportModalVisible: false,
    urlHelperModalVisible: false,
  };

  componentDidMount() {
    const { order } = this.props;
    this.changeTrackingSubscription = this.changeTrackingSubject$.pipe(debounceTime(1000)).subscribe({
      next: ({ itemNumber, trackingNumber, trackingUrl, trackingCourier }) => {
        const { setTrackingNumber, order } = this.props;
        setTrackingNumber(order.id, itemNumber, trackingCourier, trackingNumber, trackingUrl);
      },
    });
    if (order) {
      this.setTrackingState(order);
    }
  }

  componentWillUnmount() {
    if (this.changeTrackingSubject$) {
      this.changeTrackingSubject$.unsubscribe();
    }
    if (this.changeTrackingSubscription) {
      this.changeTrackingSubscription.unsubscribe();
    }
  }

  handleCloseReportModal = () => {
    this.setState({ reportModalVisible: false });
  };

  handleOpenReportModal = () => {
    this.setState({ reportModalVisible: true });
  };

  handleOpenUrlHelperModal = () => {
    this.setState({ urlHelperModalVisible: true });
  };

  handleCloseUrlHelperModal = () => {
    this.setState({ urlHelperModalVisible: false });
  };

  setTrackingState(order: Order) {
    order.items.forEach((item: OrderItem) => {
      this.setState((state: State) => ({
        ...state,
        itemValues: {
          ...state.itemValues,
          [`${item.number}`]: {
            trackingNumber: item.tracking ? item.tracking.trackingNumber : "",
            trackingUrl: item.tracking ? item.tracking.trackingUrl : "",
          },
        },
      }));
    });
  }

  handleChange = (e: Event, item: Item) => {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      this.setState((st: State) => update(st, {
        itemValues: {
          [`${item.number}`]: {
            $set: {
              ...st.itemValues[`${item.number}`],
              [name]: value,
            },
          },
        },
      }));
      const { trackingNumber, trackingUrl } = this.state.itemValues[`${item.number}`];
      if (name === 'trackingNumber') {
        if (trackingUrl) {
          this.changeTrackingSubject$.next({
            itemNumber: item.number,
            trackingNumber: value,
            trackingUrl,
            trackingCourier: 'AMZL',
          });
        } else if (!value.startsWith('TBA')) {
          this.changeTrackingSubject$.next({
            itemNumber: item.number,
            trackingNumber: value,
            trackingCourier: 'OTHER',
          });
        }
      } else if (name === 'trackingUrl') {
        this.changeTrackingSubject$.next({
          itemNumber: item.number,
          trackingNumber,
          trackingUrl: value,
          trackingCourier: 'AMZL',
        });
      }
    }
  };

  renderContent = (item: OrderItem) => {
    const { itemValues } = this.state;
    const { itemTrackingNumberLoadingMap, itemTrackingNumberErrorMap } = this.props;
    const trackingUrlError: ?HTTPError = itemTrackingNumberErrorMap[`${item.number}`]
    let trackingUrlLabel = undefined;
    if (
      trackingUrlError &&
      trackingUrlError.code === 400 &&
      trackingUrlError.data &&
      trackingUrlError.data['message'] === 'Invalid URL'
    ) {
      trackingUrlLabel = FormValidator.buildErrorLabel(
        ['InvalidTrackingNumber'],
        {
          InvalidTrackingNumber: 'order.purchase.courierFlow.products.trackingUrl.invalidTrackingNumber',
        },
      );
    }
    return (
      <Fragment>
        {!!item.externalInvoice && <SingleProductViewFile fileUrl={item.externalInvoice.image}
                                                          titleIntl="singleProduct.invoice"/>}
        <SingleProductRule/>
        <div className={style.inputControl}>
          <div className={style.textWrap}>
            <FormattedMessage id="order.purchase.courierFlow.products.trackingNumber.title">{
              text => <p className={style.title}>{text}:</p>
            }</FormattedMessage>
            <FormattedMessage id="order.purchase.courierFlow.products.title.required">{
              txt => <span>{txt}</span>
            }</FormattedMessage>
          </div>
          <FormattedMessage id="order.purchase.courierFlow.products.trackingNumber.placeholder">{
            txt => <input type="text"
                          name="trackingNumber"
                          className={classNames(inputStyle.input)}
                          placeholder={txt}
                          value={itemValues[`${item.number}`] ? itemValues[`${item.number}`].trackingNumber : ""}
                          onChange={(e: Event) => this.handleChange(e, item)}/>
          }</FormattedMessage>
          {itemTrackingNumberLoadingMap[`${item.number}`] && <Loading type="sk-three-bounce"
                                                                      color="lightBlue"
                                                                      number={3}
                                                                      className={style.loading}/>}
        </div>
        {item.onlineStore.id === "ST04287850" &&
        <div className={style.inputControl}>
          <div className={style.textWrap}>
            <FormattedMessage id="order.purchase.courierFlow.products.trackingUrl.title">{
              text => <div className={style.titleWrap}>
                <p className={style.title}>{text}:</p>
                <div className={style.helperWrap}
                     onClick={this.handleOpenUrlHelperModal}
                >
                  <div className={style.helperIcon}>?</div>
                </div>
              </div>
            }</FormattedMessage>
            <FormattedMessage id="order.purchase.courierFlow.products.title.required">{
              txt => <span>{txt}</span>
            }</FormattedMessage>
          </div>
          <FormattedMessage id="order.purchase.courierFlow.products.trackingUrl.placeholder">{
            txt => <input type="text"
                          name="trackingUrl"
                          className={classNames(
                            inputStyle.input,
                            { [inputStyle.red]: !!trackingUrlLabel },
                          )}
                          placeholder={txt}
                          value={
                            (itemValues[`${item.number}`] && itemValues[`${item.number}`].trackingUrl)
                              ? itemValues[`${item.number}`].trackingUrl : ""
                          }
                          disabled={itemValues[`${item.number}`] && !itemValues[`${item.number}`].trackingNumber}
                          onChange={(e: Event) => this.handleChange(e, item)}/>
          }</FormattedMessage>
          {itemTrackingNumberLoadingMap[`${item.number}`] && <Loading type="sk-three-bounce"
                                                                      color="lightBlue"
                                                                      number={3}
                                                                      className={style.loading}/>}
          {trackingUrlLabel}
        </div>}
      </Fragment>
    );
  };

  render() {
    const { buildStatusBoard, buildProducts, buildSummary, order, motives, locale } = this.props;
    const { reportModalVisible, urlHelperModalVisible } = this.state;
    const courier: Courier = (order: Order).shipments[0].courier;
    const d = `${courier.deliveryDays}-${courier.deliveryDays + 3}`;

    const options = motives ? motives.purchaser.map((motive: Motive) => ({
      value: motive.name,
      label: motive.translations[locale.locale.toUpperCase()],
    })) : [];

    return (
      <Fragment>
        <main className={commonStyle.mainWrap}>
          <section className={classNames(commonStyle.wrap, commonStyle.leftWrap)}>
            {courier.id === "CR09043495" ?
              <FormattedMessage id="order.purchase.courierFlow.stateInfo.subtitle.1">{
                txt => <FormattedMessage id="order.purchase.courierFlow.stateInfo.subtitle.2">{
                  txt2 => <StateInfoBox titleIntl="order.purchase.courierFlow.stateInfo.title"
                                        subtitleExtra={`${txt} ${d} ${txt2}`}
                                        image={courierFlowImage}/>}</FormattedMessage>
              }</FormattedMessage>
              : <FormattedMessage id="order.purchase.courierFlow.stateInfo.specialShipping">{
                txt => <StateInfoBox titleIntl="order.purchase.courierFlow.stateInfo.title"
                                     subtitleExtra={`${txt}`}
                                     image={courierFlowImage}/>}</FormattedMessage>
            }
            {buildStatusBoard()}
            <FormattedMessage id="order.purchase.courierFlow.products.title">{
              txt => <p className={style.productTitle}>{txt}</p>
            }</FormattedMessage>
            {buildProducts(this.renderContent)}
          </section>
          <section className={classNames(commonStyle.wrap, commonStyle.rightWrap)}>
            {buildSummary()}
            <FormattedMessage id="order.purchase.inTransit.report.button">{
              txt => <button className={classNames(buttonStyle.btn, buttonStyle.redInverse, style.report)}
                             onClick={this.handleOpenReportModal}>{txt}</button>
            }</FormattedMessage>
          </section>
        </main>
        <ReportModalContainer color="lightBlue"
                              reportType="PURCHASER_TRACKING"
                              isOpen={reportModalVisible}
                              handleClose={this.handleCloseReportModal}
                              isDiy={order.isDiy}
                              id={order.id}
                              options={options}
                              successIntl="modal.report.purchaser.success.text"/>
        <Modal open={urlHelperModalVisible}
               handleClose={this.handleCloseUrlHelperModal}
               width={700}
        >
          <FormattedMessage id="order.purchase.courierFlow.urlModal.title">{
            txt => <h2 className={style.modalTitle}>{txt}</h2>
          }</FormattedMessage>
          <img
            className={style.amazonTrackingUrlImg}
            src={amazonTrackingUrlLocation}
            alt="Amazon tracking url location"
          />
        </Modal>
      </Fragment>
    );
  }
}

CourierFlow.propTypes = {
  buildStatusBoard: PropTypes.func.isRequired,
  buildProducts: PropTypes.func.isRequired,
  buildSummary: PropTypes.func.isRequired,
  order: PropTypes.object.isRequired,
  locale: PropTypes.object.isRequired,
  setTrackingNumber: PropTypes.func.isRequired,
  itemTrackingNumberLoadingMap: PropTypes.object,
  itemTrackingNumberErrorMap: PropTypes.object,
  motives: PropTypes.object,
};



// WEBPACK FOOTER //
// ./src/modules/order/Purchase/courier-flow/components/CourierFlow.jsx